import { pick, omit } from 'lodash-es';
import {
  FILTER_RULE_ENUMS,
  FILTER_OPERATOR_ENUMS,
  RESERVATIONS_TYPES,
} from '@constants';
import { SUBSCRIPTIONS_LIMIT } from '@store';

export const getSubscriptionsSearchPayload = (searchTerm = '') => {
  if (!searchTerm || !searchTerm.trim()) {
    return {
      type: FILTER_RULE_ENUMS.EmptyRuleType,
    };
  }

  const trimmedSearchTerm = searchTerm.trim();

  const fieldsToSearch = [
    'internal_id',
    'vehicle_plate',
    'vehicle_internal_id',
  ];

  const searchRule = {
    type: FILTER_RULE_ENUMS.OrRuleType,
    value: fieldsToSearch.map((field) => ({
      type: FILTER_RULE_ENUMS.CompareStringRuleType,
      operator: FILTER_OPERATOR_ENUMS.between,
      field,
      value: trimmedSearchTerm,
    })),
  };

  return {
    type: FILTER_RULE_ENUMS.AndRuleType,
    field: null,
    operator: null,
    value: [searchRule],
  };
};

export const getSubscriptionsQueryParams = (payload = {}) => {
  if (!payload?.pageSize) {
    payload.pageSize = SUBSCRIPTIONS_LIMIT;
  }
  if (!payload?.page) {
    payload.page = '1';
  }

  return pick(payload, ['page', 'pageSize', 'order', 'orderBy', 'query']);
};

export const getSubscriptionsRequestPayload = (
  payload = {},
  modalFiltersRules = null
) => {
  const fields =
    omit(payload, [
      'page',
      'pageSize',
      'order',
      'orderBy',
      'query',
      'isErfuddAlert',
    ]) || {};
  const fieldsMap = {
    deliveryStatus: 'deliveryInfo.status',
    deliveryLocation: 'deliveryInfo.location',
    procurementStage: 'shared_vehicle.procurement.currentStageId',
    reservationType: 'shared_vehicle.availability.reservationType',
    availabilityStatus: 'shared_vehicle.availability.status',
    bundleType: 'shared_bundles.bundle_type.name',
    bundleStatus: 'shared_bundles.status',
  };
  // Define delimiters for specific fields
  const fieldDelimiters = {
    bundleType: '|',
  };
  const multiFilterStringFields = [
    'deliveryLocation',
    'labels',
    'customerTypes',
    'bundleType',
  ];
  const multiFilterNumberFields = [
    'status',
    'type',
    'deliveryStatus',
    'procurementStage',
    'reservationType',
    'availabilityStatus',
    'outOfSla',
    'bundleStatus',
  ];
  const multiFilterWithNoneField = ['assignee', 'bookingAgent'];

  const { isErfuddAlert } = payload;

  const _getValue = ({ field }) => {
    if (multiFilterNumberFields.includes(field)) {
      return payload[field]?.split(',').map((item) => Number(item));
    }
    if (multiFilterStringFields.includes(field)) {
      const delimiter = fieldDelimiters[field] || ',';
      return payload[field]?.split(delimiter) || [];
    }
    if (multiFilterWithNoneField.includes(field)) {
      return (
        payload[field]?.split(',')?.map((item) => {
          return item === 'none' ? '' : item;
        }) || []
      );
    }
    return payload[field];
  };

  const _getType = ({ field }) => {
    if (
      multiFilterNumberFields.includes(field) ||
      multiFilterStringFields.includes(field) ||
      multiFilterWithNoneField.includes(field)
    ) {
      return FILTER_RULE_ENUMS.IsAnyOfRuleType;
    }
    return FILTER_RULE_ENUMS.ComparableRuleType;
  };

  let value = [];
  if (isErfuddAlert) {
    // Add ERFUDD-specific filters
    value = [
      {
        field: 'isErfuddAlert',
        operator: 0,
        type: FILTER_RULE_ENUMS.DateBetween,
        value: '10',
      },
      {
        field: 'shared_vehicle.availability.reservationType',
        operator: 0,
        type: FILTER_RULE_ENUMS.ComparableRuleType,
        value: RESERVATIONS_TYPES.booked.value,
      },
      {
        field: 'status',
        operator: 0,
        type: FILTER_RULE_ENUMS.ComparableRuleType,
        value: 1,
      },
    ];
    // Also include other filters (excluding status and reservationType which are already set above)
    const otherFields = Object.keys(fields).filter(
      (field) => field !== 'status' && field !== 'reservationType'
    );
    value.push(
      ...otherFields.map((field) => ({
        field: fieldsMap[field] || field,
        operator: FILTER_OPERATOR_ENUMS['='],
        type: _getType({ field }),
        value: _getValue({ field }),
      }))
    );
  } else {
    value = [
      ...Object.keys(fields).map((field) => ({
        field: fieldsMap[field] || field,
        operator: FILTER_OPERATOR_ENUMS['='],
        type: _getType({ field }),
        value: _getValue({ field }),
      })),
    ];
  }

  value.push(...(modalFiltersRules?.value || []));

  if (value.length) {
    return {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value,
    };
  }

  return {
    type: FILTER_RULE_ENUMS.EmptyRuleType,
  };
};
